/**
 * iter62 → iter87.199.4 — "Live games happening now" ticker.
 *
 * Renders above the DRAFT strip when ≥1 game is currently LIVE for this
 * tournament. Was a 1-game-at-a-time rotating card (5s cycle) — now a
 * continuous right-to-left scrolling ticker so multiple scorelines can
 * be glanced at without waiting for a rotation. Hover pauses the scroll.
 *
 * Layout:
 *   [LIVE label]  [── scrolling track: game chips ──]  [count · →]
 *
 * The track holds the game chips duplicated once so the CSS marquee
 * loop (translateX 0 → -50%) reads as seamless. Consumer controls the
 * scroll speed by writing `--marquee-duration` in the inline style so
 * a slate with 2 games doesn't zip and one with 12 doesn't crawl.
 *
 * A per-chip Watch/box-score button only shows on templates that
 * expose watch providers (see `templateHasWatchProviders`), and clicking
 * a chip anywhere else routes to the Schedule tab.
 */
import { useMemo, useState } from "react";
import { ArrowRight, ClipboardList } from "lucide-react";
import { templateHasWatchProviders } from "@/lib/watchProviders";
import { WatchMatchModal } from "@/components/WatchMatchModal";

// Ticker speed tuning: ~120px of chip content per second feels readable
// on mobile without being sleepy on the wider iPad breakpoints.
const PX_PER_SEC = 120;
// Rough chip width estimate for duration math (avg game chip ≈ 260px
// with the "·" delimiter). Live length adjusts if we ever swap the
// chip shape — the duration is a hint, not a physics simulation.
const APPROX_CHIP_PX = 260;
const MIN_DURATION_S = 18;
const MAX_DURATION_S = 60;

export default function LiveGameStrip({ liveEntries, onOpenSchedule, template, isSpectator }) {
    const [watchEntry, setWatchEntry] = useState(null);
    const watchEnabled = !isSpectator && templateHasWatchProviders(template);

    // Marquee duration scales with content. Duplicated track = 2× chips,
    // so total width ≈ 2 * count * APPROX_CHIP_PX. We only need to
    // cover half of that (translateX -50%) which is `count * APPROX_CHIP_PX`.
    const durationSec = useMemo(() => {
        const total = Math.max(1, liveEntries.length) * APPROX_CHIP_PX;
        const raw = total / PX_PER_SEC;
        return Math.min(MAX_DURATION_S, Math.max(MIN_DURATION_S, raw));
    }, [liveEntries.length]);

    if (liveEntries.length === 0) return null;

    const chip = (e, keyPrefix) => {
        const home = e.home_label || e.home_name || "TBD";
        const away = e.away_label || e.away_name || "TBD";
        const score = (e.score_a != null && e.score_b != null) ? `${e.score_a}–${e.score_b}` : "vs";
        const detail = e.live_detail || "LIVE";
        return (
            <span
                key={`${keyPrefix}-${e.id || e.match_id || `${home}-${away}`}`}
                data-testid={keyPrefix === "a" ? `live-ticker-chip-${e.id || e.match_id || home}` : undefined}
                className="inline-flex items-center gap-2 shrink-0"
            >
                <button
                    type="button"
                    onClick={onOpenSchedule}
                    className="inline-flex items-center gap-2 text-sm text-white font-bold hover:text-neon transition-colors"
                    title="Open the Schedule tab"
                >
                    <span className="truncate max-w-[10rem]">{home}</span>
                    <span className="text-cyber font-mono">{score}</span>
                    <span className="truncate max-w-[10rem]">{away}</span>
                    <span className="text-xs font-mono text-zinc-400">· {detail}</span>
                </button>
                {watchEnabled && (
                    <button
                        type="button"
                        onClick={() => setWatchEntry(e)}
                        className="text-neon/70 hover:text-neon transition-colors"
                        title="Open box score"
                        aria-label={`Open box score for ${home} vs ${away}`}
                    >
                        <ClipboardList className="w-3.5 h-3.5" />
                    </button>
                )}
                <span className="text-zinc-700 select-none" aria-hidden="true">•</span>
            </span>
        );
    };

    return (
        <div
            data-testid="dashboard-live-strip"
            className="w-full flex items-center gap-3 px-3 py-2 bg-neon/10 border-2 border-neon rounded-md overflow-hidden"
        >
            {/* Fixed LIVE badge on the left */}
            <span className="inline-flex items-center gap-1.5 shrink-0">
                <span className="w-1.5 h-1.5 rounded-full bg-neon animate-pulse" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-neon">LIVE</span>
            </span>

            {/* Scrolling track. Content duplicated for a seamless loop.
                `whitespace-nowrap` + `w-max` keeps every chip on one row
                so the translate math works cleanly. */}
            <div
                data-testid="live-ticker-track"
                className="relative flex-1 min-w-0 overflow-hidden"
                aria-label={`${liveEntries.length} live ${liveEntries.length === 1 ? "game" : "games"}`}
            >
                <div
                    className="flex w-max animate-marquee whitespace-nowrap"
                    style={{ "--marquee-duration": `${durationSec}s` }}
                >
                    <div className="flex items-center gap-3 pr-6">
                        {liveEntries.map((e) => chip(e, "a"))}
                    </div>
                    {/* Duplicate track for the seamless loop */}
                    <div className="flex items-center gap-3 pr-6" aria-hidden="true">
                        {liveEntries.map((e) => chip(e, "b"))}
                    </div>
                </div>
            </div>

            {/* Fixed count + arrow on the right */}
            <button
                type="button"
                onClick={onOpenSchedule}
                data-testid="live-ticker-schedule-btn"
                className="shrink-0 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-neon hover:text-white transition-colors"
                title="Open Schedule"
                aria-label="Open Schedule"
            >
                <span className="hidden sm:inline">{liveEntries.length} live</span>
                <ArrowRight className="w-3.5 h-3.5" />
            </button>
            {watchEnabled && (
                <WatchMatchModal
                    open={!!watchEntry}
                    onClose={() => setWatchEntry(null)}
                    template={template}
                    entry={watchEntry}
                />
            )}
        </div>
    );
}
